import {
	ExternalUiVaultConfig,
	IExternalVaultApiAdapter,
	VaultManagerConfig,
	VaultStats,
} from 'src/@types/vaults';
import {
	BigNum,
	QUOTE_PRECISION_EXP,
	UserStatsAccount,
	ZERO,
} from '@velocity-exchange/sdk';
import { GET_DEFAULT_VAULT_STATS, VAULT_MANAGER_IMG_BUCKET } from '../misc';
import {
	Vault,
	VAULT_SHARES_PRECISION_EXP,
} from '@velocity-exchange/vaults-sdk';
import { MarketId, UIMarket } from '@velocity-exchange/common';

const BACKEND_API_ENDPOINT = '/api/vaults/elemental';

const ELEMENTAL_VAULT_MANAGER: VaultManagerConfig = {
	name: 'Elemental',
	imgSrc: `${VAULT_MANAGER_IMG_BUCKET}/elemental_square.webp`,
	twitterHandle: 'elementaldefi',
};

type ElementalApiVault = {
	fund_name: string;
	apy: number;
	apr: number;
	tvl: number;
	capacity: number;
	total_earnings: number;
	cumulative_return: number;
	max_daily_drawdown: number;
	lastUpdated: string;
};

class ElementalApiAdapter implements IExternalVaultApiAdapter {
	/**
	 * Static fetchPromise shared across all instances to deduplicate API calls
	 * to the same endpoint. Cleared after settling so subsequent refetch cycles
	 * get fresh data.
	 */
	private static fetchPromise: Promise<ElementalApiVault[]> | null = null;

	constructor(
		private readonly fundName: string,
		private readonly marketIndex: number,
		private readonly isUncappedCapacity: boolean
	) {}

	async getVaultStats(
		getOraclePriceForMarket: (marketId: MarketId) => BigNum | undefined
	): Promise<VaultStats | undefined> {
		const spotMarketConfig = UIMarket.spotMarkets[this.marketIndex];

		if (!spotMarketConfig) {
			return GET_DEFAULT_VAULT_STATS(ZERO);
		}

		try {
			if (!ElementalApiAdapter.fetchPromise) {
				ElementalApiAdapter.fetchPromise = fetch(BACKEND_API_ENDPOINT)
					.then((response) => response.json())
					.catch((err) => {
						console.error('ElementalApiAdapter ~ fetch ~ err:', err);
						return [];
					})
					.finally(() => {
						ElementalApiAdapter.fetchPromise = null;
					});
			}

			const data = await ElementalApiAdapter.fetchPromise;

			const fundData = data.find((fund) => fund.fund_name === this.fundName);

			if (!fundData) {
				return GET_DEFAULT_VAULT_STATS(spotMarketConfig.precisionExp);
			}

			const tvlBase = BigNum.fromPrint(
				fundData.tvl.toString(),
				spotMarketConfig.precisionExp
			);
			const oraclePrice = getOraclePriceForMarket(
				MarketId.createSpotMarket(this.marketIndex)
			);
			const tvlQuote = oraclePrice?.eqZero()
				? BigNum.zero(QUOTE_PRECISION_EXP)
				: tvlBase.mul(oraclePrice ?? ZERO).shiftTo(QUOTE_PRECISION_EXP);

			const totalBasePnl = BigNum.fromPrint(
				fundData.total_earnings.toString(),
				spotMarketConfig.precisionExp
			);
			const totalQuotePnl = totalBasePnl
				.mul(oraclePrice ?? BigNum.zero(QUOTE_PRECISION_EXP))
				.shiftTo(QUOTE_PRECISION_EXP);

			return {
				capacityPct: fundData.capacity,
				isUncappedCapacity: this.isUncappedCapacity,
				tvlQuote,
				tvlBase,
				totalBasePnl,
				totalQuotePnl,
				volume30Days: BigNum.zero(QUOTE_PRECISION_EXP),
				isOnChainStatsLoaded: true,
				totalShares: BigNum.zero(VAULT_SHARES_PRECISION_EXP),
				vaultRedeemPeriodSecs: ZERO,
				apys: {
					'7d': fundData.apy ?? 0,
					'30d': fundData.apy ?? 0,
					'90d': fundData.apy ?? 0,
					'180d': fundData.apy ?? 0,
					'365d': fundData.apy ?? 0,
				},
				maxDrawdownPct: fundData.max_daily_drawdown,
				numOfVaultSnapshots: 0,
				notionalGrowthQuotePnl: BigNum.zero(QUOTE_PRECISION_EXP),
				hasLoadedOffChainStats: true,
				hasLoadedOnChainStats: true,
				profitShare: 0,
				vaultAccount: undefined as unknown as Vault,
				vaultUserStatsAccount: undefined as unknown as UserStatsAccount,
				externalExtraStats: {
					rois: {
						// TODO :: Need elemental to provide this data
						'7d': null as unknown as number,
						'30d': null as unknown as number,
						'90d': null as unknown as number,
					},
				},
				hasSyncedWithOraclePrice: true,
			};
		} catch (err) {
			console.error('ElementalApiAdapter.getVaultStats ~ err:', err);
			return GET_DEFAULT_VAULT_STATS(spotMarketConfig.precisionExp);
		}
	}
}

export const ELEMENTAL_LEND = new ExternalUiVaultConfig({
	name: 'Elemental Lend',
	vaultManager: ELEMENTAL_VAULT_MANAGER,
	vaultPubkeyString: 'elemental_lend',
	vaultManagerPubkeyString: '',
	driftUserPubkeyString: '',
	description:
		'Elemental Lend maximizes depositor yields by aggregating top Solana opportunities, enhancing returns with proprietary strategies, and sharing surplus profits back with users.',
	depositAsset: 0,
	externalDataApiAdapter: new ElementalApiAdapter('ELEND', 0, false),
	externalUiLink: 'https://elemental.fund',
	featured: false,
	startTs: 1760572800, // 16 Oct 2024
});

export const ELEMENTAL_USDC_1_FUND = new ExternalUiVaultConfig({
	name: 'USDC 1 Fund',
	vaultManager: ELEMENTAL_VAULT_MANAGER,
	vaultPubkeyString: 'elemental_usdc_1_fund',
	vaultManagerPubkeyString: '',
	driftUserPubkeyString: '',
	description:
		"A delta-neutral fund that generates consistent fixed yields through funding rates on Drift's perpetuals, using an innovative Compound Fund structure to deliver stable returns while minimizing re-balancing needs and maintaining resilience during market stress.",
	depositAsset: 0,
	externalDataApiAdapter: new ElementalApiAdapter('USDC1', 0, false),
	externalUiLink:
		'https://elemental.fund/deposit?address=2PCrP4HgxgQQLos6N8Sd9Mx9eaW9rW1JTDqvUCcZs73p',
	featured: false,
	startTs: 1721606400, // 22 Jul 2024
});

export const ELEMENTAL_USDC_2_FUND = new ExternalUiVaultConfig({
	name: 'USDC 2 Fund',
	vaultManager: ELEMENTAL_VAULT_MANAGER,
	vaultPubkeyString: 'elemental_usdc_2_fund',
	vaultManagerPubkeyString: '',
	driftUserPubkeyString: '',
	description:
		"A delta-neutral fund that generates consistent fixed yields through funding rates on Drift's perpetuals, using an innovative Compound Fund structure to deliver stable returns while minimizing re-balancing needs and maintaining resilience during market stress.",
	depositAsset: 0,
	externalDataApiAdapter: new ElementalApiAdapter('USDC2', 0, false),
	externalUiLink:
		'https://elemental.fund/deposit?address=6AFM7XpFUhsttohknsZMyncJo7ofyVMkhwLZm5TfedNu',
	featured: false,
	startTs: 1735862400, // 3 Jan 2025
});

export const ELEMENTAL_USDC_3_FUND = new ExternalUiVaultConfig({
	name: 'USDC 3 Fund',
	vaultManager: ELEMENTAL_VAULT_MANAGER,
	vaultPubkeyString: 'elemental_usdc_3_fund',
	vaultManagerPubkeyString: '',
	driftUserPubkeyString: '',
	description:
		"A delta-neutral fund that generates consistent fixed yields through funding rates on Drift's perpetuals, using an innovative Compound Fund structure to deliver stable returns while minimizing re-balancing needs and maintaining resilience during market stress.",
	depositAsset: 0,
	externalDataApiAdapter: new ElementalApiAdapter('USDC3', 0, false),
	externalUiLink:
		'https://elemental.fund/deposit?address=4BZ1bzoiQPzRPcHAC8GgyJipHuV9fY3ZPXvnjQe635DF',
	featured: false,
	startTs: 1753747200, // 29 Jul 2025
});

export const ELEMENTAL_SOL_1_FUND = new ExternalUiVaultConfig({
	name: 'SOL 1 Fund',
	vaultManager: ELEMENTAL_VAULT_MANAGER,
	vaultPubkeyString: 'elemental_sol_1_fund',
	vaultManagerPubkeyString: '',
	driftUserPubkeyString: '',
	description:
		"A delta-neutral fund that generates consistent fixed yields through funding rates on Drift's perpetuals, using an innovative Compound Fund structure to deliver stable returns while minimizing re-balancing needs and maintaining resilience during market stress.",
	depositAsset: 1,
	externalDataApiAdapter: new ElementalApiAdapter('SOL1', 1, false),
	externalUiLink:
		'https://elemental.fund/?address=idFPEqqaLM2sJKbCqub8DsEnDRAPFKeoiSqktAj5dE7',
	startTs: 1729814400, // 25 Oct 2024
});

export const ELEMENTAL_SOL_2_FUND = new ExternalUiVaultConfig({
	name: 'SOL 2 Fund',
	vaultManager: ELEMENTAL_VAULT_MANAGER,
	vaultPubkeyString: 'elemental_sol_2_fund',
	vaultManagerPubkeyString: '',
	driftUserPubkeyString: '',
	description:
		"A delta-neutral fund that generates consistent fixed yields through funding rates on Drift's perpetuals, using an innovative Compound Fund structure to deliver stable returns while minimizing re-balancing needs and maintaining resilience during market stress.",
	depositAsset: 1,
	externalDataApiAdapter: new ElementalApiAdapter('SOL2', 1, false),
	externalUiLink:
		'https://elemental.fund/?address=EAkv2wHmNtVpBPujHPP6gkaYLkGhJ2Z59GmgNvXKe73F',
	startTs: 1736985600, // 16 Jan 2025
});
